import {
	AtlBrowserStorageLocal,
	AtlBrowserStorageSession,
} from '@atlassian/browser-storage-controls/web-storage-service';
import { fg } from '@atlassian/jira-feature-gating';
import { STOREJS_KEY_PREFIX } from './constants.tsx';
import {
	type SetItemResult,
	type WebStorageProvider,
	type WebStorageProviderNew,
	WebStorageType,
} from './types.tsx';

import { isStorageAPIAvailable } from './isStorageAPIAvailable.tsx';

export const createStorageProvider = (
	storageType: (typeof WebStorageType)[keyof typeof WebStorageType],
	appPrefix: string | undefined,
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	store: any,
): WebStorageProvider | WebStorageProviderNew => {
	const normalizedAppPrefix = appPrefix ?? '';
	const namespacedStore = store.namespace(normalizedAppPrefix);
	// Uses the same key format as store.js: namespace ? '__storejs_' + namespace + '_' + key : key
	const getKeyWithPrefix = (key: string) =>
		normalizedAppPrefix.trim().length ? `${STOREJS_KEY_PREFIX}${normalizedAppPrefix}_${key}` : key;

	const storageAPI =
		storageType === WebStorageType.LocalStorage ? AtlBrowserStorageLocal : AtlBrowserStorageSession;

	const browserStorage =
		storageType === WebStorageType.LocalStorage
			? globalThis.localStorage
			: globalThis.sessionStorage;

	const remove = (key: string) => {
		try {
			if (fg('jfp-vulcan-browser-storage-migration')) {
				if (!isStorageAPIAvailable()) {
					// eslint-disable-next-line no-console
					console.warn(`${storageType} is unavailable (e.g. SSR). Failed to remove item ${key}`);
					return;
				}
				const prefixedKey = getKeyWithPrefix(key);
				storageAPI.removeItem(prefixedKey); // remove data from new store
				namespacedStore.remove(key); // remove data from old store as well
			} else {
				namespacedStore.remove(key);
			}
		} catch (err) {
			// eslint-disable-next-line no-console
			console.error(`Error removing item with key=${key}:`, err);
		}
	};

	return {
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		set: (key: string, value: any) => {
			try {
				// Matches store.js behavior: set(key, undefined) calls remove(key)
				if (value === undefined) {
					return remove(key);
				}
				if (fg('jfp-vulcan-browser-storage-migration')) {
					if (!isStorageAPIAvailable()) {
						// eslint-disable-next-line no-console
						console.warn(`${storageType} is unavailable (e.g. SSR). Failed to set item ${key}`);
						return;
					}
					const prefixedKey = getKeyWithPrefix(key);
					storageAPI.setItem(prefixedKey, JSON.stringify(value)).then((setItemResult) => {
						if (setItemResult?.status === 'BLOCKED') {
							storageAPI.removeItem(prefixedKey);
						}
					});
				} else {
					namespacedStore.set(key, value);
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error(`Error setting item with key=${key}:`, err);
			}
		},
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		setAsync: async (key: string, value: any): Promise<void> => {
			try {
				if (value === undefined) {
					return remove(key);
				}
				if (fg('jfp-vulcan-browser-storage-migration')) {
					if (!isStorageAPIAvailable()) {
						// eslint-disable-next-line no-console
						console.warn(`${storageType} is unavailable (e.g. SSR). Failed to set item ${key}`);
						return;
					}
					const prefixedKey = getKeyWithPrefix(key);
					const setItemResult: SetItemResult = await storageAPI.setItem(
						prefixedKey,
						JSON.stringify(value),
					);
					if (setItemResult !== undefined) {
						if (setItemResult.status === 'BLOCKED') {
							storageAPI.removeItem(prefixedKey);
							throw new Error('write was blocked by storage or consent preferences.');
						}
						if (setItemResult.status !== 'SUCCESS') {
							throw new Error('write failed');
						}
					}
				} else {
					namespacedStore.set(key, value);
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error(`Error setting item with key=${key}:`, err);
				throw err;
			}
		},

		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		setStrictlyNecessaryItem: (key: string, value: any) => {
			try {
				// Matches store.js behavior: set(key, undefined) calls remove(key)
				if (value === undefined) {
					return remove(key);
				}
				if (fg('jfp-vulcan-browser-storage-migration')) {
					if (!isStorageAPIAvailable()) {
						// eslint-disable-next-line no-console
						console.warn(
							`${storageType} is unavailable (e.g. SSR). Failed to set strictly necessary item ${key}`,
						);
						return;
					}
					const prefixedKey = getKeyWithPrefix(key);
					storageAPI.setStrictlyNecessaryItem(prefixedKey, JSON.stringify(value));
				} else {
					// this has to be done when setStrictlyNecessaryItem comes into usage, and it should have a fallback when FG is turned off
					namespacedStore.set(key, value);
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error(`Error setting item with key=${key}:`, err);
			}
		},
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		get: (key: string): any | undefined => {
			try {
				if (fg('jfp-vulcan-browser-storage-migration')) {
					if (!isStorageAPIAvailable()) {
						// eslint-disable-next-line no-console
						console.warn(`${storageType} is unavailable (e.g. SSR). Failed to get item ${key}`);
						return undefined;
					}
					const prefixedKey = getKeyWithPrefix(key);
					const item = storageAPI.getItem(prefixedKey);
					if (typeof item === 'string') {
						return JSON.parse(item);
					}
				} else {
					return namespacedStore.get(key);
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error(`Error getting item with key=${key}:`, err);
			}
		},
		remove,
		// Clears the complete storage
		clearAll: () => {
			try {
				if (fg('jfp-vulcan-browser-storage-migration')) {
					if (!isStorageAPIAvailable()) {
						// eslint-disable-next-line no-console
						console.warn(`${storageType} is unavailable (e.g. SSR). Failed to clear storage`);
						return;
					}
					storageAPI.clear();
					// clean up old keys too
					namespacedStore.clearAll();
				} else {
					namespacedStore.clearAll();
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error('Error clearing storage:', err);
			}
		},
		length: (): number | undefined => {
			try {
				if (!isStorageAPIAvailable()) {
					// eslint-disable-next-line no-console
					console.warn(`${storageType} is unavailable (e.g. SSR). Failed to get storage length`);
					return 0;
				}
				if (fg('jfp-vulcan-browser-storage-migration')) {
					return storageAPI.length();
				}
				return browserStorage?.length || 0;
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error('Error getting storage length:', err);
				return 0;
			}
		},
		key: (index: number): string | null => {
			try {
				if (!isStorageAPIAvailable()) {
					// eslint-disable-next-line no-console
					console.warn(
						`${storageType} is unavailable (e.g. SSR). Failed to get key at index ${index}`,
					);
					return null;
				}
				if (fg('jfp-vulcan-browser-storage-migration')) {
					return storageAPI.key(index);
				}
				return browserStorage?.key(index) ?? null;
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error('Error getting key at index:', err);
				return null;
			}
		},
	};
};
